import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Generic localStorage helpers for persisted page filters.
 *
 * Convention: one storage key per feature, holding a single object with one
 * nested key per tab. e.g. `atek-invoices-filters` →
 *   { tab: 'list', list: { search, status, … }, board: { quarter, … } }
 *
 * The hook below is a thin wrapper — most pages use `loadPersistedFilters` /
 * `savePersistedFilters` directly. Use `usePersistedTab` for the active-tab
 * piece since that's the most repetitive.
 */

export function makeFilterStorage<T extends Record<string, unknown>>(storageKey: string) {
  function load(): Partial<T> {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '{}');
    } catch {
      return {};
    }
  }

  function save(patch: Partial<T>) {
    try {
      const current = load();
      localStorage.setItem(storageKey, JSON.stringify({ ...current, ...patch }));
    } catch {
      // localStorage unavailable
    }
  }

  return { load, save };
}

/**
 * Persist the active page-level tab to `atek_<feature>_active_tab`.
 * Restores in the lazy `useState` initializer to avoid flash-of-default.
 *
 * Usage:
 *   const [tab, setTab] = usePersistedTab('invoices', VALID_TABS, 'list');
 */
export function usePersistedTab<T extends string>(
  feature: string,
  validTabs: readonly T[],
  defaultTab: T,
): [T, (next: T) => void] {
  const key = `atek_${feature}_active_tab`;

  const [tab, setTab] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(key) as T | null;
      return saved && validTabs.includes(saved) ? saved : defaultTab;
    } catch {
      return defaultTab;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(key, tab); } catch { /* storage is best-effort; ignore (private mode / quota) */ }
  }, [tab, key]);

  return [tab, setTab];
}

/**
 * Bind a piece of state to a slot inside the persisted filter object.
 *
 * Example — single search field on the "list" tab:
 *   const filters = useMemo(() => makeFilterStorage<{ list: { search?: string } }>('atek-invoices-filters'), []);
 *   const [search, setSearch] = usePersistedField(filters, 'list', 'search', '');
 */
export function usePersistedField<
  T extends Record<string, Record<string, unknown>>,
  TabKey extends keyof T,
  FieldKey extends keyof T[TabKey],
>(
  storage: ReturnType<typeof makeFilterStorage<T>>,
  tab: TabKey,
  field: FieldKey,
  defaultValue: T[TabKey][FieldKey],
): [T[TabKey][FieldKey], (next: T[TabKey][FieldKey]) => void] {
  const initial = useMemo(() => {
    const all = storage.load();
    const tabSlot = (all[tab] ?? {}) as T[TabKey];
    return (tabSlot[field] as T[TabKey][FieldKey] | undefined) ?? defaultValue;
  }, [storage, tab, field, defaultValue]);

  const [value, setValue] = useState<T[TabKey][FieldKey]>(initial);

  const set = useCallback(
    (next: T[TabKey][FieldKey]) => {
      setValue(next);
      const all = storage.load();
      const tabSlot = (all[tab] ?? {}) as T[TabKey];
      const nextTab = { ...tabSlot, [field]: next } as T[TabKey];
      storage.save({ [tab]: nextTab } as unknown as Partial<T>);
    },
    [storage, tab, field],
  );

  return [value, set];
}
